import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styles from './RNAProcessedOutputFileListPopup.module.css';
import ReportModal from '../../components/modal/ReportModal';

const RNAProcessedOutputFileListPopup = ({
    onClose,        // 닫기 함수
    processedFiles, // Expression 파일 목록 (부모에서 전달받음)
    activeTab,      // 현재 활성화된 프로젝트 탭 (Lung, Colon 등)
    TABS,           // 탭 정의 객체
    t               // 번역 함수
}) => {
    // 내부 탭 상태: 'expression' | 'deg'
    const [viewType, setViewType] = useState('deg');

    // 리포트 오픈 변수 및 선택된 샘플명 상태 (누락분 추가)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [selectedReportSample, setSelectedReportSample] = useState('');

    // 리포트 팝업 열기 함수
    const handleOpenReport = (sampleName) => {
        setSelectedReportSample(sampleName); // 돋보기 클릭 시 폴더명을 샘플명으로 세팅
        setIsReportModalOpen(true);
    };

    // 검색어 상태
    const [searchQuery, setSearchQuery] = useState('');

    // DEG 파일 목록 상태
    const [degFiles, setDegFiles] = useState([]);
    const [isLoadingDeg, setIsLoadingDeg] = useState(false);

    // [Expression] 필터링 로직
    // 1. 현재 프로젝트 탭(activeTab) 이름으로 시작하는 파일만
    // 2. 검색어 필터 적용
    const filteredExpressionFiles = useMemo(() => {
        const currentProjectName = TABS[activeTab]; // 예: 'Lung', 'Colon'
        return processedFiles
            .filter((file) => file.startsWith(currentProjectName))
            .filter((file) => file.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [processedFiles, activeTab, TABS, searchQuery]);

    // [DEG] 탭 진입 시 목록 가져오기
    useEffect(() => {
        if (viewType === 'deg') {
            fetchDegFiles();
        }
    }, [viewType]);

    // [DEG] 파일 목록 가져오기 함수
    const fetchDegFiles = async () => {
        setIsLoadingDeg(true);
        try {
            // 현재 탭 이름 (Lung, Colon...) 전송
            const projectName = TABS[activeTab];
            const response = await axios.get(`/api/files/deg?project=${projectName}`);
            // 백엔드는 [{ folderName: 'DEG_Lung_...' }, ...] 형태로 반환
            setDegFiles(response.data.files || []);
        } catch (error) {
            console.error('Failed to fetch DEG files:', error);
            setDegFiles([]);
        } finally {
            setIsLoadingDeg(false);
        }
    };

    // [DEG] 필터링 로직 (폴더명 검색)
    const filteredDegFiles = useMemo(() => {
        if (!searchQuery) return degFiles;
        return degFiles.filter(item =>
            item.folderName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [degFiles, searchQuery]);


    return (
        <>
            {/* 반투명 오버레이 */}
            <div
                className={styles.drawerOverlay}
                onClick={onClose}
                aria-hidden
            />

            {/* 오른쪽 드로어 */}
            <aside
                className={styles.drawer}
                role="dialog"
                aria-labelledby="processedTitle"
                aria-modal="true"
            >
                {/* 헤더 */}
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerTitleWrap}>
                        <span className={styles.drawerTitleIcon} aria-hidden>≣</span>
                        <h3 id="processedTitle" className={styles.drawerTitle}>
                            {t('Analysis Results')}
                        </h3>
                    </div>
                    <button
                        type="button"
                        className={styles.drawerClose}
                        onClick={onClose}
                        aria-label={t('닫기')}
                    >
                        ×
                    </button>
                </div>

                {/* 탭 메뉴 (Expression / DEG) */}
                <div className={styles.tabContainer}>
                    <button
                        className={`${styles.tabButton} ${viewType === 'deg' ? styles.activeTab : ''}`}
                        onClick={() => { setViewType('deg'); setSearchQuery(''); }}
                    >
                        DEG
                    </button>
                    <button
                        className={`${styles.tabButton} ${viewType === 'expression' ? styles.activeTab : ''}`}
                        onClick={() => { setViewType('expression'); setSearchQuery(''); }}
                    >
                        Expression
                    </button>
                </div>

                <div className={styles.drawerBody}>
                    {/* 검색창 */}
                    <div className={styles.searchBox}>
                        <label htmlFor="po-search" className={styles.searchLabel}>
                            {t('Search')}
                        </label>
                        <input
                            id="po-search"
                            type="text"
                            className={styles.searchInput}
                            // placeholder={t(viewType === 'expression' ? '파일명 검색' : '분석명 검색')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* [VIEW 2] DEG Folder List */}
                    {viewType === 'deg' && (
                        <div className={styles.degListContainer}>
                            {isLoadingDeg ? (
                                <div className={styles.loadingMsg}>{t('로딩 중...')}</div>
                            ) : (
                                <ul className={styles.degFolderList}>
                                    {filteredDegFiles.map((item) => (
                                        <li key={item.folderName} className={styles.degFolderItem}>
                                            <div className={styles.folderRow}>
                                                <div className={styles.folderInfo}>
                                                    <span className={styles.folderIcon}>📦</span>
                                                    <span className={styles.folderName} title={item.folderName}>
                                                        {item.folderName}
                                                    </span>
                                                </div>

                                                {/* ★ 액션 버튼 영역 (보고서 열기 + ZIP 다운로드) */}
                                                <div className={styles.actionButtons}>
                                                    <button
                                                        type="button"
                                                        className={styles.reportBtn}
                                                        onClick={() => handleOpenReport(item.folderName)}
                                                        title="보고서 열기"
                                                    >
                                                        🔍
                                                    </button>
                                                    <a
                                                        href={`/api/files/deg/download/${item.folderName}`}
                                                        download
                                                        className={styles.downloadBtn}
                                                        title="Download Folder (ZIP)"
                                                    >
                                                        {t("ZIP 다운로드")}
                                                    </a>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                    {filteredDegFiles.length === 0 && (
                                        <li className={styles.emptyMsg}>{t('해당 프로젝트의 분석 결과가 없습니다.')}</li>
                                    )}
                                </ul>
                            )}
                        </div>
                    )}


                    {/* [VIEW 1] Expression File List */}
                    {viewType === 'expression' && (
                        <ul className={styles.drawerFileList}>
                            {filteredExpressionFiles.map((file) => (
                                <li key={file} className={styles.drawerFileItem}>
                                    <div className={styles.fileRow}>
                                        <span className={styles.fileName}>{file}</span>
                                        <a
                                            href={`/api/files/processed/download/${file}`}
                                            download
                                            className={styles.downloadIconBtn}
                                            title="Download"
                                        >
                                            ⬇
                                        </a>
                                    </div>
                                </li>
                            ))}
                            {filteredExpressionFiles.length === 0 && (
                                <li className={styles.emptyMsg}>{t('결과 파일이 없습니다.')}</li>
                            )}
                        </ul>
                    )}

                </div>

                {/* ★ 선택된 폴더명을 넘겨주는 Report Modal */}
                <ReportModal
                    isOpen={isReportModalOpen}
                    onClose={() => setIsReportModalOpen(false)}
                    sampleName={selectedReportSample}
                    reportType="DEG" // 필요에 따라 기존처럼 WES로 변경 가능
                />
            </aside>
        </>
    );
};

export default RNAProcessedOutputFileListPopup;